/**
 * Inventory name parsing for display.
 *
 * Warehouse (ShipBob) inventory names are free text with the mattress/bedding
 * size embedded anywhere in the string — "Super King Cooling Mattress
 * Protector (UK)", "Dosaze™ Contoured Orthopedic Pillow - KING (CA)",
 * "Bamboo Poly Sheet Set - Cream / Twin XL (CA)". There is no structured size
 * field upstream, so the size is extracted here for display.
 */

// Longest-first so "King Single" wins over "King", "Twin XL" over "Twin".
const SIZE_TOKENS = [
  "Three Quarter/Queen",
  "California King",
  "Three Quarter",
  "Small Double",
  "Small Single",
  "King Single",
  "Super King",
  "Cal King",
  "Twin XL",
  "TwinXL",
  "Standard",
  "Emperor",
  "Double",
  "Single",
  "Queen",
  "King",
  "Full",
  "Twin",
];

const SIZE_ALTERNATION = SIZE_TOKENS.map((t) => t.replace(/ /g, "\\s+")).join("|");

const SIZE_PATTERN = new RegExp("\\b(" + SIZE_ALTERNATION + ")\\b", "i");

// A dash/comma-delimited size at the end of the name ("ThermaCool Pillow
// Bundle - KING") is the item's own size; prefer it over an earlier mention
// (e.g. a bundle listing its contents).
const SIZE_SUFFIX_PATTERN = new RegExp("[-,]\\s*(" + SIZE_ALTERNATION + ")\\s*$", "i");

// The card renders the country on its own line, so the trailing geo tag in the
// raw name ("(AUS)", "(CA)", "(UK)", "(US)") is redundant noise.
const GEO_SUFFIX_PATTERN = /\s*\((AUS|CA|UK|US)\)\s*$/i;

function normalizeSize(raw: string): string {
  const token = raw.replace(/\s+/g, " ").trim();
  if (token.toUpperCase().replace(" ", "") === "TWINXL") return "Twin XL";
  return token.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export type ParsedInventoryName = {
  /** The name with the size and trailing geo tag removed. */
  name: string;
  /** Normalized size ("Super King", "Twin XL"), or null if none found. */
  size: string | null;
};

export function parseInventoryName(rawName: string | null): ParsedInventoryName {
  if (!rawName) return { name: "", size: null };
  const withoutGeo = rawName.replace(GEO_SUFFIX_PATTERN, "").trim();

  let pattern = SIZE_SUFFIX_PATTERN;
  let match = pattern.exec(withoutGeo);
  if (!match) {
    pattern = SIZE_PATTERN;
    match = pattern.exec(withoutGeo);
  }
  if (!match) return { name: withoutGeo, size: null };

  const cleaned = withoutGeo
    .replace(pattern, " ")
    .replace(/\(\s*\)/g, " ") // "(King)" leaves empty parens behind
    .replace(/\s+/g, " ")
    .trim()
    .replace(/([-,/])\s*(?=[-,/])/g, "") // "Momme - King / Ivory" -> "Momme - / Ivory" -> "Momme / Ivory"
    .replace(/^[-,/\s]+/, "") // dangling separator where the size led the name
    .replace(/[-,/\s]+$/, ""); // dangling separator where the size ended it

  return {
    // A name that was nothing but a size keeps its original text.
    name: cleaned || withoutGeo,
    size: normalizeSize(match[1]),
  };
}
